import { useMutation } from '@tanstack/react-query'
import { adminAuthApi } from '../api/admin-auth.api'
import { authApi } from '../api/auth.api'
import { useAuthStore } from '../stores/auth.store'
import type { ApiError } from '@/lib/api'
import type { AuthUser, LoginCredentials } from '../types'

/**
 * Admin sign-in (`/admin`). Authenticates against `POST /admin/auth/login`,
 * then resolves the profile via `GET /auth/me` to determine scope: a `null`
 * tenant marks the AMLiQ super admin, otherwise the backend role applies
 * (both handled in `mapProfileToAuthUser`). The admin endpoint issues a single
 * JWT with no refresh token, so the session is established from that one token.
 * Navigation is the caller's job.
 */
export function useLogin() {
  const setSessionFromToken = useAuthStore((s) => s.setSessionFromToken)

  return useMutation<{ user: AuthUser; token: string }, ApiError, LoginCredentials>({
    mutationFn: async (credentials) => {
      const { token } = await adminAuthApi.login(credentials)
      const user = await authApi.me(token)
      return { user, token }
    },
    onSuccess: ({ user, token }) => {
      setSessionFromToken({ user, accessToken: token })
    },
  })
}
